import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CircleNotch, SignOut, Money, ClipboardText, CheckCircle, Clock, Eye,
  MusicNotesSimple, UploadSimple, Trash, Tag, Plus, Copy, VideoCamera, Star,
} from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const FULFILLMENT = [
  { id: "nuevo", label: "Nuevo", color: "bg-blue-100 text-blue-700" },
  { id: "en_proceso", label: "En proceso", color: "bg-amber-100 text-amber-700" },
  { id: "en_revision", label: "En revisión", color: "bg-purple-100 text-purple-700" },
  { id: "entregado", label: "Entregado", color: "bg-green-100 text-green-700" },
];

const PAY_BADGE = {
  paid: "bg-green-100 text-green-700",
  pending: "bg-gray-100 text-gray-600",
  failed: "bg-red-100 text-red-700",
  expired: "bg-red-100 text-red-700",
};

const renderDaysRemaining = (o) => {
  if (o.payment_status !== "paid") {
    return <span className="text-xs font-semibold text-foreground/30">Sin pagar</span>;
  }
  if (o.fulfillment_status === "entregado") {
    return <span className="rounded-full bg-green-100 text-green-700 px-2.5 py-1 text-xs font-bold">Entregado</span>;
  }
  const d = o.days_remaining;
  if (d === null || d === undefined) return <span className="text-foreground/40">—</span>;
  let cls = "bg-green-100 text-green-700";
  let label;
  if (d < 0) {
    cls = "bg-red-100 text-red-700";
    label = `Atrasado ${Math.abs(d)}d`;
  } else if (d === 0) {
    cls = "bg-red-100 text-red-700";
    label = "¡Hoy!";
  } else if (d <= 2) {
    cls = "bg-amber-100 text-amber-700";
    label = d === 1 ? "1 día" : `${d} días`;
  } else {
    label = `${d} días`;
  }
  return <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${cls}`}>{label}</span>;
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-3xl bg-white p-6 shadow-glass border border-white">
    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: color }}>
      <Icon weight="duotone" size={26} className="text-foreground" />
    </div>
    <div className="font-display font-bold text-3xl text-foreground">{value}</div>
    <div className="text-sm font-semibold text-foreground/50">{label}</div>
  </div>
);

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingKaraoke, setUploadingKaraoke] = useState(false);
  const [uploadingMp32, setUploadingMp32] = useState(false);
  const [showcase, setShowcase] = useState([]);
  const [scForm, setScForm] = useState({ title: "", genre: "", voice: "femenina", occasion: "", description: "" });
  const [scFile, setScFile] = useState(null);
  const [scUploading, setScUploading] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [genreSamples, setGenreSamples] = useState([]);
  const [uploadingGenre, setUploadingGenre] = useState(null);
  const [affName, setAffName] = useState("");
  const [affCustomCode, setAffCustomCode] = useState("");
  const [affKind, setAffKind] = useState("amount");
  const [affPayoutMethod, setAffPayoutMethod] = useState("zelle");
  const [affPayoutAccount, setAffPayoutAccount] = useState("");
  const [affCreating, setAffCreating] = useState(false);

  useEffect(() => {
    if (admin === false) navigate("/admin/login");
  }, [admin, navigate]);

  const load = async () => {
    try {
      const [o, s, sc] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/admin/stats"),
        api.get("/admin/showcase"),
      ]);
      setOrders(o.data);
      setStats(s.data);
      setShowcase(sc.data);
      try {
        const af = await api.get("/admin/affiliates");
        setAffiliates(af.data);
      } catch { /* ignore */ }
      try {
        const rv = await api.get("/admin/reviews");
        setReviews(rv.data);
      } catch { /* ignore */ }
      try {
        const gs = await api.get("/admin/genre-samples");
        setGenreSamples(gs.data);
      } catch { /* ignore */ }
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  const uploadShowcase = async (e) => {
    e.preventDefault();
    if (!scFile || !scForm.title.trim()) {
      toast.error("Agrega un título y un archivo de audio");
      return;
    }
    setScUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", scFile);
      Object.entries(scForm).forEach(([k, v]) => fd.append(k, v));
      await api.post("/admin/showcase", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Ejemplo publicado");
      setScForm({ title: "", genre: "", voice: "femenina", occasion: "", description: "" });
      setScFile(null);
      const sc = await api.get("/admin/showcase");
      setShowcase(sc.data);
    } catch (err) {
      toast.error("No se pudo subir el ejemplo");
    } finally {
      setScUploading(false);
    }
  };

  const deleteShowcase = async (id) => {
    try {
      await api.delete(`/admin/showcase/${id}`);
      setShowcase((prev) => prev.filter((s) => s.id !== id));
      toast.success("Ejemplo eliminado");
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  const createAffiliate = async (e) => {
    e.preventDefault();
    if (!affName.trim()) {
      toast.error("Escribe el nombre del afiliado");
      return;
    }
    if (affKind === "amount" && !affPayoutAccount.trim()) {
      toast.error("Ingresa la cuenta del afiliado para el pago de comisiones");
      return;
    }
    setAffCreating(true);
    try {
      const { data } = await api.post("/admin/affiliates", {
        affiliate_name: affName.trim(),
        code: affCustomCode.trim(),
        kind: affKind,
        payout_method: affKind === "amount" ? affPayoutMethod : null,
        payout_account: affKind === "amount" ? affPayoutAccount.trim() : null,
      });
      toast.success(`Código creado: ${data.code}`);
      setAffName("");
      setAffCustomCode("");
      setAffKind("amount");
      setAffPayoutMethod("zelle");
      setAffPayoutAccount("");
      const af = await api.get("/admin/affiliates");
      setAffiliates(af.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear el afiliado");
    } finally {
      setAffCreating(false);
    }
  };

  const toggleAffiliate = async (id) => {
    try {
      const { data } = await api.patch(`/admin/affiliates/${id}`);
      setAffiliates((prev) => prev.map((a) => (a.id === id ? { ...a, is_active: data.is_active } : a)));
    } catch (err) {
      toast.error("No se pudo actualizar");
    }
  };

  const deleteAffiliate = async (id) => {
    try {
      await api.delete(`/admin/affiliates/${id}`);
      setAffiliates((prev) => prev.filter((a) => a.id !== id));
      toast.success("Afiliado eliminado");
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  const markCommissionPaid = async (id) => {
    try {
      await api.post(`/admin/affiliates/${id}/commission-paid`);
      setAffiliates((prev) => prev.map((a) => (a.id === id ? { ...a, commission_pending: 0 } : a)));
      toast.success("Comisión marcada como pagada");
    } catch (err) {
      toast.error("No se pudo actualizar la comisión");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Código ${code} copiado`);
  };

  const toggleReview = async (id) => {
    try {
      const { data } = await api.patch(`/admin/reviews/${id}`);
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, approved: data.approved } : r)));
      toast.success(data.approved ? "Reseña aprobada y publicada" : "Reseña ocultada");
    } catch (err) {
      toast.error("No se pudo actualizar la reseña");
    }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Reseña eliminada");
    } catch (err) {
      toast.error("No se pudo eliminar la reseña");
    }
  };

  useEffect(() => { if (admin) load(); }, [admin]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { fulfillment_status: status });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, fulfillment_status: status } : o)));
      if (selected?.id === id) setSelected((s) => ({ ...s, fulfillment_status: status }));
      toast.success("Estado actualizado");
    } catch (e) {
      toast.error("No se pudo actualizar");
    }
  };

  const updateStage = async (id, stage) => {
    try {
      await api.patch(`/admin/orders/${id}/stage`, { stage });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, stage } : o)));
      if (selected?.id === id) setSelected((s) => ({ ...s, stage }));
      toast.success("Etapa actualizada");
    } catch (e) {
      toast.error("No se pudo actualizar la etapa");
    }
  };

  const uploadGenreSample = async (genre, file) => {
    if (!file) return;
    setUploadingGenre(genre);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/genre-samples/${genre}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGenreSamples((prev) => prev.map((g) => (g.genre === genre ? { ...g, filename: data.filename, has_sample: true } : g)));
      toast.success("Muestra subida");
    } catch (e) {
      toast.error("No se pudo subir la muestra");
    } finally {
      setUploadingGenre(null);
    }
  };

  const deleteGenreSample = async (genre) => {
    try {
      await api.delete(`/admin/genre-samples/${genre}`);
      setGenreSamples((prev) => prev.map((g) => (g.genre === genre ? { ...g, filename: null, has_sample: false } : g)));
      toast.success("Muestra eliminada");
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const uploadMp3 = async (id, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/orders/${id}/upload-mp3`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      applyUploadPatch(id, { mp3_filename: data.mp3_filename, mp3_path: "uploaded" });
      toast.success("MP3 subido correctamente");
    } catch (e) {
      toast.error("No se pudo subir el archivo");
    } finally {
      setUploading(false);
    }
  };

  const uploadKaraoke = async (id, file) => {
    if (!file) return;
    setUploadingKaraoke(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/orders/${id}/upload-karaoke`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      applyUploadPatch(id, { karaoke_filename: data.karaoke_filename, karaoke_path: "uploaded" });
      toast.success("Video karaoke subido correctamente");
    } catch (e) {
      toast.error("No se pudo subir el archivo");
    } finally {
      setUploadingKaraoke(false);
    }
  };

  const uploadMp32 = async (id, file) => {
    if (!file) return;
    setUploadingMp32(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/orders/${id}/upload-mp3-2`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      applyUploadPatch(id, { mp3_filename2: data.mp3_filename2, mp3_path2: "uploaded" });
      toast.success("Segundo MP3 subido correctamente");
    } catch (e) {
      toast.error("No se pudo subir el archivo");
    } finally {
      setUploadingMp32(false);
    }
  };

  const toggleMp3Multiple = async (id, enabled) => {
    try {
      await api.patch(`/admin/orders/${id}/mp3-multiple`, { enabled });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, mp3_multiple: enabled } : o)));
      if (selected?.id === id) setSelected((s) => ({ ...s, mp3_multiple: enabled }));
    } catch (e) {
      toast.error("No se pudo actualizar");
    }
  };

  // Aplica el archivo subido y recalcula si el pedido quedó completo (entregado)
  const applyUploadPatch = (id, patch) => {
    const isComplete = (o) =>
      !!o.mp3_path
      && (!o.mp3_multiple || !!o.mp3_path2)
      && (!o.karaoke || !!o.karaoke_path);
    setOrders((prev) => prev.map((o) => {
      if (o.id !== id) return o;
      const merged = { ...o, ...patch };
      return { ...merged, fulfillment_status: isComplete(merged) ? "entregado" : merged.fulfillment_status };
    }));
    if (selected?.id === id) {
      setSelected((s) => {
        const merged = { ...s, ...patch };
        return { ...merged, fulfillment_status: isComplete(merged) ? "entregado" : merged.fulfillment_status };
      });
    }
  };

  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.length === orders.length ? [] : orders.map((o) => o.id)));
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/orders/bulk-delete", { ids: selectedIds });
      setOrders((prev) => prev.filter((o) => !selectedIds.includes(o.id)));
      setSelectedIds([]);
      toast.success(`Se eliminaron ${data.deleted} pedidos`);
      load();
    } catch (e) {
      toast.error("No se pudieron eliminar los pedidos seleccionados");
    } finally {
      setDeleting(false);
    }
  };

  const deleteAllOrders = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete("/admin/orders");
      setOrders([]);
      setSelectedIds([]);
      setStats((s) => (s ? { ...s, total: 0, paid: 0, revenue: 0, pending_fulfillment: 0 } : s));
      toast.success(`Se borraron ${data.deleted} pedidos`);
      load();
    } catch (e) {
      toast.error("No se pudieron borrar los pedidos");
    } finally {
      setDeleting(false);
    }
  };

  if (admin === null || loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><CircleNotch className="animate-spin text-primary" size={48} /></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="glass border-b border-white/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[70px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="logo" className="h-10" />
            <span className="font-display font-semibold text-foreground hidden sm:block">Panel de pedidos</span>
          </div>
          <button
            data-testid="admin-logout"
            onClick={() => { logout(); navigate("/admin/login"); }}
            className="flex items-center gap-2 rounded-full glass border border-white/60 px-4 py-2 text-sm font-bold text-foreground/70"
          >
            <SignOut size={18} /> Salir
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <StatCard icon={ClipboardText} label="Pedidos totales" value={stats.total} color="hsl(200 75% 82%)" />
            <StatCard icon={CheckCircle} label="Pagados" value={stats.paid} color="hsl(140 60% 82%)" />
            <StatCard icon={Money} label="Ingresos (USD)" value={`$${stats.revenue}`} color="hsl(351 100% 85%)" />
            <StatCard icon={Clock} label="Por entregar" value={stats.pending_fulfillment} color="hsl(45 100% 82%)" />
          </div>
        )}

        <div className="rounded-3xl bg-white shadow-glass border border-white overflow-hidden">
          <div className="p-6 border-b border-border flex items-center justify-between gap-4">
            <h2 className="font-display font-semibold text-xl text-foreground">Pedidos</h2>
            <div className="flex items-center gap-2">
              {selectedIds.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      data-testid="delete-selected-btn"
                      disabled={deleting}
                      className="flex items-center gap-2 rounded-full bg-red-600 text-white px-4 py-2 text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-40"
                    >
                      {deleting ? <CircleNotch className="animate-spin" size={16} /> : <Trash weight="bold" size={16} />}
                      Eliminar seleccionados ({selectedIds.length})
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-3xl" data-testid="delete-selected-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display text-2xl">¿Eliminar {selectedIds.length} pedido(s)?</AlertDialogTitle>
                      <AlertDialogDescription className="text-foreground/60 font-medium">
                        Esta acción eliminará <strong>permanentemente</strong> los pedidos seleccionados. No se puede deshacer.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-full font-bold" data-testid="delete-selected-cancel">Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="delete-selected-confirm"
                        onClick={deleteSelected}
                        className="rounded-full bg-red-600 hover:bg-red-700 font-bold"
                      >
                        Sí, eliminar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    data-testid="delete-all-orders-btn"
                    disabled={orders.length === 0 || deleting}
                    className="flex items-center gap-2 rounded-full bg-red-50 text-red-600 px-4 py-2 text-sm font-bold hover:bg-red-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {deleting ? <CircleNotch className="animate-spin" size={16} /> : <Trash weight="bold" size={16} />}
                    Borrar todos
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-3xl" data-testid="delete-all-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-display text-2xl">¿Borrar todos los pedidos?</AlertDialogTitle>
                    <AlertDialogDescription className="text-foreground/60 font-medium">
                      Esta acción eliminará <strong>permanentemente</strong> los {orders.length} pedidos de esta lista.
                      No se puede deshacer. Úsalo solo si quieres empezar desde cero.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full font-bold" data-testid="delete-all-cancel">Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="delete-all-confirm"
                      onClick={deleteAllOrders}
                      className="rounded-full bg-red-600 hover:bg-red-700 font-bold"
                    >
                      Sí, borrar todo
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
          {orders.length === 0 ? (
            <div className="p-16 text-center text-foreground/40 font-semibold">Aún no hay pedidos.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="orders-table">
                <thead>
                  <tr className="text-left text-foreground/50 font-bold border-b border-border">
                    <th className="px-6 py-4">
                      <input
                        data-testid="select-all-orders"
                        type="checkbox"
                        checked={orders.length > 0 && selectedIds.length === orders.length}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 accent-primary cursor-pointer"
                      />
                    </th>
                    <th className="px-6 py-4">Cliente</th>
                    <th className="px-6 py-4">Paquete</th>
                    <th className="px-6 py-4">Entrega</th>
                    <th className="px-6 py-4">Días restantes</th>
                    <th className="px-6 py-4">Total</th>
                    <th className="px-6 py-4">Pago</th>
                    <th className="px-6 py-4">Estado</th>
                    <th className="px-6 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`order-row-${o.id}`} className={`border-b border-border/60 hover:bg-muted/40 ${selectedIds.includes(o.id) ? "bg-primary/5" : ""}`}>
                      <td className="px-6 py-4">
                        <input
                          data-testid={`select-order-${o.id}`}
                          type="checkbox"
                          checked={selectedIds.includes(o.id)}
                          onChange={() => toggleSelect(o.id)}
                          className="w-4 h-4 accent-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-bold text-foreground">{o.contact_name}</div>
                        <div className="text-xs text-foreground/50">{o.contact_email}</div>
                      </td>
                      <td className="px-6 py-4 font-semibold text-foreground/80">{o.package_name}</td>
                      <td className="px-6 py-4 text-foreground/60">{o.delivery_name}</td>
                      <td className="px-6 py-4" data-testid={`days-remaining-${o.id}`}>{renderDaysRemaining(o)}</td>
                      <td className="px-6 py-4 font-display font-bold text-foreground">${o.amount}</td>
                      <td className="px-6 py-4">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${PAY_BADGE[o.payment_status] || PAY_BADGE.pending}`}>{o.payment_status}</span>
                      </td>
                      <td className="px-6 py-4 min-w-[160px]">
                        <Select value={o.fulfillment_status} onValueChange={(v) => updateStatus(o.id, v)}>
                          <SelectTrigger data-testid={`status-select-${o.id}`} className="h-9 rounded-full text-xs font-bold border-border">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FULFILLMENT.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-4">
                        <button data-testid={`view-order-${o.id}`} onClick={() => setSelected(o)} className="text-foreground/50 hover:text-primary"><Eye size={20} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-3xl bg-white shadow-glass border border-white overflow-hidden mt-8">
          <div className="p-6 border-b border-border flex items-center gap-2">
            <MusicNotesSimple weight="duotone" size={22} className="text-primary" />
            <h2 className="font-display font-semibold text-xl text-foreground">Ejemplos públicos (galería)</h2>
          </div>
          <div className="p-6 grid lg:grid-cols-2 gap-8">
            <form onSubmit={uploadShowcase} className="space-y-3" data-testid="showcase-form">
              <p className="text-sm font-bold text-foreground/50">Publicar un nuevo ejemplo</p>
              <input
                data-testid="showcase-title"
                value={scForm.title}
                onChange={(e) => setScForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Título (ej. Para mamá con amor)"
                className="w-full rounded-2xl border-2 border-border bg-white px-4 py-2.5 font-medium focus:border-primary outline-none text-sm"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="showcase-genre"
                  value={scForm.genre}
                  onChange={(e) => setScForm((f) => ({ ...f, genre: e.target.value }))}
                  placeholder="Género"
                  className="w-full rounded-2xl border-2 border-border bg-white px-4 py-2.5 font-medium focus:border-primary outline-none text-sm"
                />
                <Select value={scForm.voice} onValueChange={(v) => setScForm((f) => ({ ...f, voice: v }))}>
                  <SelectTrigger data-testid="showcase-voice" className="rounded-2xl border-2 border-border h-[46px] text-sm font-medium">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="femenina">Voz femenina</SelectItem>
                    <SelectItem value="masculina">Voz masculina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <input
                data-testid="showcase-occasion"
                value={scForm.occasion}
                onChange={(e) => setScForm((f) => ({ ...f, occasion: e.target.value }))}
                placeholder="Ocasión (ej. Cumpleaños)"
                className="w-full rounded-2xl border-2 border-border bg-white px-4 py-2.5 font-medium focus:border-primary outline-none text-sm"
              />
              <textarea
                data-testid="showcase-description"
                value={scForm.description}
                onChange={(e) => setScForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Descripción breve (opcional)"
                rows={2}
                className="w-full rounded-2xl border-2 border-border bg-white px-4 py-2.5 font-medium focus:border-primary outline-none text-sm resize-none"
              />
              <label className="flex items-center gap-2 rounded-2xl border-2 border-dashed border-border px-4 py-2.5 text-sm font-semibold text-foreground/60 cursor-pointer hover:border-primary/50">
                <UploadSimple weight="bold" size={16} />
                {scFile ? scFile.name : "Seleccionar audio (MP3)"}
                <input
                  data-testid="showcase-file"
                  type="file"
                  accept="audio/*,.mp3"
                  className="hidden"
                  onChange={(e) => setScFile(e.target.files?.[0] || null)}
                />
              </label>
              <button
                data-testid="showcase-submit"
                type="submit"
                disabled={scUploading}
                className="flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 font-bold text-primary-foreground shadow-soft disabled:opacity-60 text-sm"
              >
                {scUploading ? <CircleNotch className="animate-spin" size={16} /> : <UploadSimple weight="bold" size={16} />}
                Publicar ejemplo
              </button>
            </form>

            <div className="space-y-3">
              <p className="text-sm font-bold text-foreground/50">Ejemplos publicados ({showcase.length})</p>
              {showcase.length === 0 ? (
                <p className="text-sm text-foreground/40 py-6">Aún no hay ejemplos. Sube uno para mostrarlo en la web.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {showcase.map((s) => (
                    <div key={s.id} data-testid={`showcase-item-${s.id}`} className="flex items-center gap-3 rounded-2xl bg-muted p-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-foreground text-sm truncate">{s.title}</div>
                        <div className="text-xs text-foreground/50 truncate">{[s.genre, s.voice, s.occasion].filter(Boolean).join(" · ")}</div>
                      </div>
                      <audio controls preload="none" src={`${API}/showcase/${s.id}/audio`} className="h-8 max-w-[150px]" />
                      <button
                        data-testid={`showcase-delete-${s.id}`}
                        onClick={() => deleteShowcase(s.id)}
                        className="flex-shrink-0 text-red-500 hover:text-red-700"
                        aria-label="Eliminar ejemplo"
                      >
                        <Trash weight="bold" size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white shadow-glass border border-white overflow-hidden mt-8">
          <div className="p-6 border-b border-border flex items-center gap-2">
            <Tag weight="duotone" size={22} className="text-primary" />
            <h2 className="font-display font-semibold text-xl text-foreground">Códigos de afiliados</h2>
            <span className="text-sm font-semibold text-foreground/40">· $1 de descuento · 10% de comisión</span>
          </div>
          <div className="p-6 grid lg:grid-cols-2 gap-8">
            <form onSubmit={createAffiliate} className="space-y-3" data-testid="affiliate-form">
              <p className="text-sm font-bold text-foreground/50">Crear un nuevo código</p>
              <input
                data-testid="affiliate-name"
                value={affName}
                onChange={(e) => setAffName(e.target.value)}
                placeholder="Nombre del afiliado (ej. María López)"
                className="w-full rounded-2xl border-2 border-border bg-white px-4 py-2.5 font-medium focus:border-primary outline-none text-sm"
              />
              <input
                data-testid="affiliate-custom-code"
                value={affCustomCode}
                onChange={(e) => setAffCustomCode(e.target.value.toUpperCase())}
                placeholder="Código personalizado (opcional, se genera solo)"
                className="w-full rounded-2xl border-2 border-border bg-white px-4 py-2.5 font-medium uppercase focus:border-primary outline-none text-sm"
              />
              <div className="space-y-2">
                <label className={`flex items-start gap-2 rounded-2xl border-2 p-3 cursor-pointer ${affKind === "amount" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <input type="radio" name="affKind" data-testid="affiliate-kind-amount" checked={affKind === "amount"} onChange={() => setAffKind("amount")} className="mt-1 accent-primary" />
                  <span className="text-sm"><span className="font-bold text-foreground">$1 de descuento</span><span className="block text-xs text-foreground/50">Aplica a cualquier compra, reutilizable. Genera 10% de comisión para el afiliado.</span></span>
                </label>
                <label className={`flex items-start gap-2 rounded-2xl border-2 p-3 cursor-pointer ${affKind === "free" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <input type="radio" name="affKind" data-testid="affiliate-kind-free" checked={affKind === "free"} onChange={() => setAffKind("free")} className="mt-1 accent-primary" />
                  <span className="text-sm"><span className="font-bold text-foreground">100% gratis</span><span className="block text-xs text-foreground/50">Solo Plan Mini + entrega estándar (sin extras). Un solo uso.</span></span>
                </label>
              </div>
              {affKind === "amount" && (
                <div className="space-y-2 rounded-2xl border-2 border-border p-3" data-testid="affiliate-payout-fields">
                  <p className="text-xs font-bold text-foreground/50">Datos para pagar la comisión (10%)</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[["zelle", "Zelle"], ["venmo", "Venmo"], ["cashapp", "CashApp"]].map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        data-testid={`affiliate-payout-${val}`}
                        onClick={() => setAffPayoutMethod(val)}
                        className={`rounded-xl border-2 px-2 py-2 text-xs font-bold transition-colors ${affPayoutMethod === val ? "border-primary bg-primary/10 text-primary" : "border-border text-foreground/60"}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <input
                    data-testid="affiliate-payout-account"
                    value={affPayoutAccount}
                    onChange={(e) => setAffPayoutAccount(e.target.value)}
                    placeholder={affPayoutMethod === "zelle" ? "Correo o teléfono de Zelle" : affPayoutMethod === "venmo" ? "Usuario de Venmo (@usuario)" : "Cashtag de CashApp ($usuario)"}
                    className="w-full rounded-2xl border-2 border-border bg-white px-4 py-2.5 font-medium focus:border-primary outline-none text-sm"
                  />
                </div>
              )}
              <button
                data-testid="affiliate-submit"
                type="submit"
                disabled={affCreating}
                className="flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 font-bold text-primary-foreground shadow-soft disabled:opacity-60 text-sm"
              >
                {affCreating ? <CircleNotch className="animate-spin" size={16} /> : <Plus weight="bold" size={16} />}
                Crear código
              </button>
              <p className="text-xs text-foreground/40">Si dejas el código vacío, el sistema generará uno único automáticamente.</p>
            </form>

            <div className="space-y-3">
              <p className="text-sm font-bold text-foreground/50">Afiliados ({affiliates.length})</p>
              {affiliates.length === 0 ? (
                <p className="text-sm text-foreground/40 py-6">Aún no hay códigos. Crea uno para tus afiliados.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {affiliates.map((a) => (
                    <div key={a.id} data-testid={`affiliate-item-${a.id}`} className="flex items-center gap-3 rounded-2xl bg-muted p-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-display font-bold text-foreground">{a.code}</span>
                          <button
                            data-testid={`affiliate-copy-${a.id}`}
                            onClick={() => copyCode(a.code)}
                            className="text-foreground/40 hover:text-primary"
                            aria-label="Copiar código"
                          >
                            <Copy size={15} />
                          </button>
                          {a.kind === "free" && <span className="rounded-full bg-purple-100 text-purple-700 px-2 py-0.5 text-[10px] font-bold">100% GRATIS · 1 uso</span>}
                          {a.kind === "amount" && a.commission_pending > 0 && (
                            <span data-testid={`affiliate-commission-badge-${a.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${a.commission_pending >= 20 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                              Comisión ${a.commission_pending.toFixed(2)}
                            </span>
                          )}
                          {!a.is_active && <span className="rounded-full bg-gray-200 text-gray-500 px-2 py-0.5 text-[10px] font-bold">{a.used ? "Usado" : "Inactivo"}</span>}
                        </div>
                        <div className="text-xs text-foreground/50 truncate">{a.affiliate_name} · {a.uses} usos · ${a.total_discount} otorgados</div>
                        {a.kind === "amount" && (a.payout_method || a.commission_total > 0) && (
                          <div className="text-[11px] text-foreground/40 truncate">
                            {a.payout_method ? `${a.payout_method.charAt(0).toUpperCase() + a.payout_method.slice(1)}: ${a.payout_account || "—"}` : ""}
                            {a.commission_total > 0 ? ` · Total comisión: $${a.commission_total.toFixed(2)}` : ""}
                          </div>
                        )}
                      </div>
                      {a.kind === "amount" && a.commission_pending > 0 && (
                        <button
                          data-testid={`affiliate-commission-paid-${a.id}`}
                          onClick={() => markCommissionPaid(a.id)}
                          className="flex-shrink-0 rounded-full px-3 py-1 text-xs font-bold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                        >
                          Comisión pagada
                        </button>
                      )}
                      <button
                        data-testid={`affiliate-toggle-${a.id}`}
                        onClick={() => toggleAffiliate(a.id)}
                        className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-bold transition-colors ${a.is_active ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}
                      >
                        {a.is_active ? "Desactivar" : "Activar"}
                      </button>
                      <button
                        data-testid={`affiliate-delete-${a.id}`}
                        onClick={() => deleteAffiliate(a.id)}
                        className="flex-shrink-0 text-red-500 hover:text-red-700"
                        aria-label="Eliminar afiliado"
                      >
                        <Trash weight="bold" size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white shadow-glass border border-white overflow-hidden mt-8">
          <div className="p-6 border-b border-border flex items-center gap-2">
            <Star weight="fill" size={22} className="text-primary" />
            <h2 className="font-display font-semibold text-xl text-foreground">Reseñas de clientes</h2>
            <span className="text-sm font-semibold text-foreground/40">· aprueba u oculta antes de publicarlas</span>
          </div>
          <div className="p-6">
            {reviews.length === 0 ? (
              <p className="text-sm text-foreground/40 py-6 text-center">Aún no hay reseñas.</p>
            ) : (
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} data-testid={`review-item-${r.id}`} className={`rounded-2xl p-4 border ${r.approved ? "bg-green-50/60 border-green-200" : "bg-muted border-border"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display font-bold text-foreground">{r.name}</span>
                          <span className="flex gap-0.5">
                            {[...Array(5)].map((_, s) => <Star key={s} weight={s < r.rating ? "fill" : "regular"} size={13} className="text-primary" />)}
                          </span>
                          {r.occasion && <span className="text-xs font-bold text-foreground/40">· {r.occasion}</span>}
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.approved ? "bg-green-200 text-green-800" : "bg-amber-100 text-amber-700"}`}>
                            {r.approved ? "Publicada" : "Pendiente"}
                          </span>
                        </div>
                        <p className="mt-1.5 text-sm text-foreground/70 leading-relaxed">{r.text}</p>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <button
                          data-testid={`review-toggle-${r.id}`}
                          onClick={() => toggleReview(r.id)}
                          className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${r.approved ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-green-600 text-white hover:bg-green-700"}`}
                        >
                          {r.approved ? "Ocultar" : "Aprobar"}
                        </button>
                        <button
                          data-testid={`review-delete-${r.id}`}
                          onClick={() => deleteReview(r.id)}
                          className="self-center text-red-500 hover:text-red-700"
                          aria-label="Eliminar reseña"
                        >
                          <Trash weight="bold" size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-3xl bg-white shadow-glass border border-white overflow-hidden mt-8">
          <div className="p-6 border-b border-border flex items-center gap-2">
            <MusicNotesSimple weight="duotone" size={22} className="text-primary" />
            <h2 className="font-display font-semibold text-xl text-foreground">Muestras de género (Preview ~10s)</h2>
            <span className="text-sm font-semibold text-foreground/40">· sube un MP3 por género para el botón Preview del formulario</span>
          </div>
          <div className="p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {genreSamples.map((g) => (
              <div key={g.genre} data-testid={`genre-sample-${g.genre}`} className={`rounded-2xl p-4 border ${g.has_sample ? "bg-green-50/60 border-green-200" : "bg-muted border-border"}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-display font-bold text-foreground capitalize">{g.genre.replace(/_/g, " ")}</span>
                  {g.has_sample && (
                    <button data-testid={`genre-delete-${g.genre}`} onClick={() => deleteGenreSample(g.genre)} className="text-red-500 hover:text-red-700" aria-label="Eliminar muestra">
                      <Trash weight="bold" size={16} />
                    </button>
                  )}
                </div>
                {g.has_sample ? (
                  <p className="text-xs font-semibold text-green-600 mb-2 truncate">✓ {g.filename}</p>
                ) : (
                  <p className="text-xs text-foreground/40 mb-2">Sin muestra</p>
                )}
                <label className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-4 py-2 font-bold text-xs cursor-pointer hover:opacity-90">
                  {uploadingGenre === g.genre ? <CircleNotch className="animate-spin" size={14} /> : <UploadSimple weight="bold" size={14} />}
                  {g.has_sample ? "Reemplazar" : "Subir MP3"}
                  <input
                    data-testid={`genre-upload-${g.genre}`}
                    type="file"
                    accept="audio/*,.mp3"
                    className="hidden"
                    disabled={uploadingGenre === g.genre}
                    onChange={(e) => uploadGenreSample(g.genre, e.target.files?.[0])}
                  />
                </label>
              </div>
            ))}
          </div>
        </div>


      </main>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Detalle del pedido</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm" data-testid="order-detail">
              {[
                ["Código", selected.tracking_code],
                ["Cliente", selected.contact_name],
                ["Correo", selected.contact_email],
                ["Paquete", selected.package_name],
                ["Entrega", selected.delivery_name],
                ["Total", `$${selected.amount} USD`],
                ["Publicar en Spotify", selected.spotify ? "Sí ✓" : "No"],
                ["Video Karaoke", selected.karaoke ? "Sí ✓" : "No"],
                ["Código afiliado", selected.affiliate_code || "—"],
                ["Género", selected.song_request?.genero],
                ["Voz", selected.song_request?.voz],
                ["Ocasión especial", selected.song_request?.ocasion_especial],
                ["Ocasión", selected.song_request?.ocasion || "—"],
                ["Incluir nombre", selected.song_request?.incluir_nombre],
                ["Nombre persona", selected.song_request?.nombre_persona || "—"],
                ["Relación", selected.song_request?.relacion || "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border pb-2">
                  <span className="font-bold text-foreground/50">{k}</span>
                  <span className="font-semibold text-foreground text-right capitalize">{v}</span>
                </div>
              ))}
              <div>
                <div className="font-bold text-foreground/50 mb-1">Mensaje / historia</div>
                <p className="rounded-2xl bg-muted p-4 text-foreground/80 font-medium">{selected.song_request?.mensaje}</p>
              </div>

              <div className="rounded-2xl border-2 border-border p-4">
                <div className="font-bold text-foreground/70 mb-2">Etapa del pedido (visible para el cliente)</div>
                <select
                  data-testid="order-stage-select"
                  value={selected.stage || "pedido_realizado"}
                  onChange={(e) => updateStage(selected.id, e.target.value)}
                  className="w-full rounded-2xl border-2 border-border bg-white px-4 py-2.5 font-semibold text-sm focus:border-primary outline-none"
                >
                  {[
                    ["pedido_realizado", "1. Pedido realizado"],
                    ["escribiendo_letra", "2. Escribiendo la letra"],
                    ["letra_entregada", "3. Letra entregada"],
                    ["revision_letra", "4. Revisión de la letra"],
                    ["letra_aprobada", "5. Letra aprobada"],
                    ["creando_melodia", "6. Creación y grabación de la melodía"],
                    ["cancion_entregada", "7. Canción entregada"],
                  ].map(([val, lbl]) => (
                    <option key={val} value={val}>{lbl}</option>
                  ))}
                </select>
              </div>

              <div className="rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bold text-foreground/70 flex items-center gap-2">
                    <MusicNotesSimple weight="duotone" size={20} className="text-primary" /> {selected.mp3_multiple ? "Audio 1 (MP3)" : "Canción final (MP3)"}
                  </div>
                  <label className="flex items-center gap-2 text-xs font-bold text-foreground/60 cursor-pointer select-none" data-testid="mp3-multiple-label">
                    <input
                      data-testid="mp3-multiple-checkbox"
                      type="checkbox"
                      checked={!!selected.mp3_multiple}
                      onChange={(e) => toggleMp3Multiple(selected.id, e.target.checked)}
                      className="w-4 h-4 accent-primary"
                    />
                    MP3 Múltiple
                  </label>
                </div>
                {selected.mp3_filename ? (
                  <p className="text-sm font-semibold text-green-600 mb-2">✓ Subido: {selected.mp3_filename}</p>
                ) : (
                  <p className="text-sm text-foreground/50 mb-2">Aún no se ha subido la canción.</p>
                )}
                <label className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 py-2.5 font-bold text-sm cursor-pointer shadow-soft hover:opacity-90">
                  {uploading ? <CircleNotch className="animate-spin" size={16} /> : <UploadSimple weight="bold" size={16} />}
                  {selected.mp3_filename ? "Reemplazar MP3" : "Subir MP3"}
                  <input
                    data-testid="mp3-upload-input"
                    type="file"
                    accept="audio/*,.mp3"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => uploadMp3(selected.id, e.target.files?.[0])}
                  />
                </label>
              </div>

              {selected.mp3_multiple && (
                <div className="rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 p-4">
                  <div className="font-bold text-foreground/70 mb-2 flex items-center gap-2">
                    <MusicNotesSimple weight="duotone" size={20} className="text-primary" /> Audio 2 (MP3)
                  </div>
                  {selected.mp3_filename2 ? (
                    <p className="text-sm font-semibold text-green-600 mb-2">✓ Subido: {selected.mp3_filename2}</p>
                  ) : (
                    <p className="text-sm text-foreground/50 mb-2">Aún no se ha subido el segundo audio.</p>
                  )}
                  <label className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 py-2.5 font-bold text-sm cursor-pointer shadow-soft hover:opacity-90">
                    {uploadingMp32 ? <CircleNotch className="animate-spin" size={16} /> : <UploadSimple weight="bold" size={16} />}
                    {selected.mp3_filename2 ? "Reemplazar audio 2" : "Subir audio 2"}
                    <input
                      data-testid="mp3-2-upload-input"
                      type="file"
                      accept="audio/*,.mp3"
                      className="hidden"
                      disabled={uploadingMp32}
                      onChange={(e) => uploadMp32(selected.id, e.target.files?.[0])}
                    />
                  </label>
                </div>
              )}


              {selected.karaoke && (
                <div className="rounded-2xl border-2 border-dashed border-secondary/50 bg-secondary/10 p-4">
                  <div className="font-bold text-foreground/70 mb-2 flex items-center gap-2">
                    <VideoCamera weight="duotone" size={20} className="text-secondary-foreground" /> Video Karaoke
                  </div>
                  {selected.karaoke_filename ? (
                    <p className="text-sm font-semibold text-green-600 mb-2">✓ Subido: {selected.karaoke_filename}</p>
                  ) : (
                    <p className="text-sm text-foreground/50 mb-2">Aún no se ha subido el video karaoke.</p>
                  )}
                  <label className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 py-2.5 font-bold text-sm cursor-pointer shadow-soft hover:opacity-90">
                    {uploadingKaraoke ? <CircleNotch className="animate-spin" size={16} /> : <UploadSimple weight="bold" size={16} />}
                    {selected.karaoke_filename ? "Reemplazar video" : "Subir video"}
                    <input
                      data-testid="karaoke-upload-input"
                      type="file"
                      accept="video/*,.mp4"
                      className="hidden"
                      disabled={uploadingKaraoke}
                      onChange={(e) => uploadKaraoke(selected.id, e.target.files?.[0])}
                    />
                  </label>
                </div>
              )}

              <p className="text-xs text-foreground/40">
                El correo de descarga se enviará automáticamente cuando todos los archivos requeridos estén subidos
                {selected.mp3_multiple ? " (Audio 1 y Audio 2" : " (MP3"}
                {selected.karaoke ? " + Video Karaoke)" : ")"}.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
